import { RotateCw } from 'lucide-react'
import React, { useState } from 'react'
import { Button } from '~/components/common/Button'
import { ProgressSpinner } from '~/components/common/ProgressSpinner'
import { SearchInput } from '~/components/common/SearchInput'
import { EditGrayIcon } from '~/components/icons'
import { useIndexes } from '~/hooks'
import { IndexStatus } from '~/types/Indexes'
import { FlatIndex, useIndexesContext } from '~/contexts/IndexesContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { updateIndexStatus } from '~/services/organizationService'
import { cm } from '~/utils/utils'
import { ConnectionUnion } from './types'

const STATUS_LABEL: Record<IndexStatus, string> = {
  [IndexStatus.INITIALIZED]: 'Queued',
  [IndexStatus.IN_PROGRESS]: 'Indexing',
  [IndexStatus.SUSPENDED]: 'Paused',
  [IndexStatus.FAILED]: 'Failed',
  [IndexStatus.SUCCEEDED]: 'Ready'
}

interface ConnectedReposTableProps {
  selectedConnection: ConnectionUnion
  onIndexSelect: (index: FlatIndex) => void
}

/**
 * Status cell renderer.
 *
 * Queued / Indexing → spinner + label (in-flight states the user is waiting on).
 * Paused / Failed / Ready → inline status icon + label (terminal states).
 *
 * The icon precedes the label and is sized to match the 14px text line height so
 * rows stay visually aligned with the all-text Runtime column to their right.
 */
function StatusCell({ status }: { status: IndexStatus }) {
  const label = STATUS_LABEL[status] ?? ''
  const isInFlight = status === IndexStatus.INITIALIZED || status === IndexStatus.IN_PROGRESS

  let icon: React.ReactNode = null
  let labelClass = 'text-[rgba(0,0,0,0.65)]'

  if (isInFlight) {
    icon = <ProgressSpinner size='sm' />
  } else if (status === IndexStatus.SUCCEEDED) {
    icon = <CheckCircleIcon className='text-[#52C41A]' />
    labelClass = 'text-[rgba(0,0,0,0.65)]'
  } else if (status === IndexStatus.FAILED) {
    icon = <ErrorCircleIcon className='text-[#FF4D4F]' />
    labelClass = 'text-[#FF4D4F]'
  } else if (status === IndexStatus.SUSPENDED) {
    icon = <PauseCircleIcon className='text-[rgba(0,0,0,0.45)]' />
    labelClass = 'text-[rgba(0,0,0,0.65)]'
  }

  return (
    <div className='flex items-center gap-1.5 text-sm leading-[18px]'>
      <span className='flex items-center justify-center w-3.5 h-3.5 shrink-0'>{icon}</span>
      <span className={labelClass}>{label}</span>
    </div>
  )
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
    >
      <path
        d='M7 0a7 7 0 1 0 0 14A7 7 0 0 0 7 0Zm3.18 5.04L6.36 9.91a.62.62 0 0 1-.95.04L3.84 8.18a.62.62 0 1 1 .94-.82l1.06 1.21 3.36-4.3a.62.62 0 0 1 .98.77Z'
        fill='currentColor'
      />
    </svg>
  )
}

function ErrorCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
    >
      <path
        d='M7 0a7 7 0 1 0 0 14A7 7 0 0 0 7 0Zm0 11.16a.84.84 0 1 1 0-1.68.84.84 0 0 1 0 1.68Zm.7-3.5a.7.7 0 0 1-1.4 0V3.78a.7.7 0 0 1 1.4 0v3.88Z'
        fill='currentColor'
      />
    </svg>
  )
}

function PauseCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
    >
      <path
        d='M7 0a7 7 0 1 0 0 14A7 7 0 0 0 7 0ZM5.95 9.45a.45.45 0 0 1-.45.45h-.6a.45.45 0 0 1-.45-.45v-4.9a.45.45 0 0 1 .45-.45h.6a.45.45 0 0 1 .45.45v4.9Zm3.6 0a.45.45 0 0 1-.45.45h-.6a.45.45 0 0 1-.45-.45v-4.9a.45.45 0 0 1 .45-.45h.6a.45.45 0 0 1 .45.45v4.9Z'
        fill='currentColor'
      />
    </svg>
  )
}

function buildRuntimeText(index: FlatIndex): string {
  const versions: string[] = []
  if (index.java_version) versions.push(`Java ${index.java_version}`)
  if (index.maven_version) versions.push(`Maven ${index.maven_version}`)
  if (index.mule_runtime_version) versions.push(`Mule ${index.mule_runtime_version}`)
  return versions.join(', ') || '—'
}

// ── Columned-list layout (private to this table) ──
// Formerly the shared `common/ColumnedList`, folded in here (CUR-1454) since the
// connected-repos table was its only consumer. Header row + spaced, clickable
// rounded rows; the last column grows and right-aligns for the actions slot.
interface ColumnDef {
  /** Header label rendered in the header row. */
  header?: React.ReactNode
  /** Classes merged into the cell `<div>` — applies to the header + every row cell at this index. */
  className?: string
  /** Inline styles for the cell `<div>`, usually `width` / `minWidth`. */
  style?: React.CSSProperties
}

const ColumnsContext = React.createContext<ColumnDef[]>([])

// Last column grows and right-aligns (actions slot). All others are fixed-width,
// truncate, and sit `ml-6` apart; the first column has no left margin so it lines
// up with the row padding.
const cellLayoutClasses = (idx: number, total: number) => {
  const isLast = idx === total - 1
  const isFirst = idx === 0
  return cm(
    isLast ? 'ml-12 mr-4 grow flex items-center justify-end' : 'truncate',
    !isLast && !isFirst && 'ml-6',
    !isLast && 'text-left'
  )
}

function ColumnedList({
  columns,
  children,
  className
}: {
  columns: ColumnDef[]
  children?: React.ReactNode
  className?: string
}) {
  const showHeader = columns.some(c => c.header !== undefined)
  return (
    <ColumnsContext.Provider value={columns}>
      <div className={cm('w-full', className)}>
        {showHeader && (
          <div className='flex items-center h-10 px-4'>
            {columns.map((col, idx) => (
              <div
                key={idx}
                className={cm(
                  'font-mono font-semibold text-sm text-[rgba(0,0,0,0.65)] leading-4',
                  cellLayoutClasses(idx, columns.length),
                  col.className
                )}
                style={col.style}
              >
                {col.header}
              </div>
            ))}
          </div>
        )}
        <div className='space-y-4'>{children}</div>
      </div>
    </ColumnsContext.Provider>
  )
}

function ColumnedListItem({ cells, onClick }: { cells: React.ReactNode[]; onClick: () => void }) {
  const columns = React.useContext(ColumnsContext)
  const [isPressed, setIsPressed] = useState(false)

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <div
      role='button'
      tabIndex={0}
      data-testid='repo-row'
      className={cm(
        'flex items-center px-4 h-10 rounded-[12px] cursor-pointer',
        'transition-colors duration-150 ease-in-out outline-none',
        isPressed ? 'bg-[rgba(0,0,0,0.06)]' : 'hover:bg-[rgba(0,0,0,0.04)]'
      )}
      onClick={onClick}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      onKeyDown={handleKeyDown}
    >
      {cells.map((cell, idx) => {
        const col = columns[idx] ?? {}
        const isFirst = idx === 0
        return (
          <div
            key={idx}
            className={cm(
              'text-base font-medium text-[rgba(0,0,0,0.88)] leading-[22px]',
              isFirst && 'font-bold leading-[18px]',
              cellLayoutClasses(idx, cells.length),
              col.className
            )}
            style={col.style}
          >
            {cell}
          </div>
        )
      })}
    </div>
  )
}

const INDEX_LIST_COLUMNS: ColumnDef[] = [
  {
    header: 'Repository name',
    className: 'select-none',
    style: { minWidth: '102px', width: '40%' }
  },
  {
    header: 'Status',
    className: 'select-none',
    style: { width: '120px', flexShrink: 0 }
  },
  {
    header: 'Runtime',
    className: 'select-none',
    style: { flexShrink: 0 }
  },
  {
    header: '',
    className: 'select-none'
  }
]

export function ConnectedReposTable({
  selectedConnection,
  onIndexSelect
}: ConnectedReposTableProps) {
  const indexes = useIndexes()
  const { refetch, isLoading, isFetching } = useIndexesContext()
  const { currentWorkspace } = useWorkspaceContext()
  const [searchKeyword, setSearchKeyword] = useState<string>('')

  // Match conn_type case-insensitively. Backend stores lowercase (e.g. 'github'), but if
  // any historical row drifts in case the strict === comparison would silently hide the row.
  const selectedId = selectedConnection!.id.toLowerCase()
  const connectionIndexes = indexes.filter(
    index => (index.conn.conn_type ?? '').toLowerCase() === selectedId
  )
  // Show indexes in every status — previously only SUCCEEDED were rendered, which made
  // INITIALIZED/IN_PROGRESS/SUSPENDED/FAILED repos invisible. The dedicated Status column
  // (col2) tells the user what state each row is in.
  const filteredIndexes = connectionIndexes.filter(index => {
    const searchText = `${index.org_name}/${index.repo_name}/${index.branch_name}`.toLowerCase()
    return searchText.includes(searchKeyword.toLowerCase())
  })

  // Re-queue failed repos: the poller gives up on a repo it can't clone by marking it
  // FAILED, which drops it from the pollable set. Setting it back to INITIALIZED puts it
  // back in the queue. Retry-all targets every failed repo for this connection (not just
  // the search-filtered subset) so it isn't silently scoped by an active search.
  const failedIndexes = connectionIndexes.filter(index => index.status === IndexStatus.FAILED)

  // Body states. Without these the table renders just its header (a blank list)
  // while the indexes query is in flight — most visibly in the few seconds after
  // a bulk "select repos" save, when the refetch is running but query.data is
  // still the previous (empty) set, so it looks like nothing happened.
  //   loading: a fetch is in flight and we have no rows yet for this connection
  //            (covers first load AND the post-save refetch).
  //   empty:   fetch settled and this connection genuinely has no repos.
  //   noMatch: this connection has repos but the search filtered them all out.
  const isFetchingIndexes = isLoading || isFetching
  const showLoading = isFetchingIndexes && connectionIndexes.length === 0
  const showEmpty = !isFetchingIndexes && connectionIndexes.length === 0
  const showNoSearchMatches = connectionIndexes.length > 0 && filteredIndexes.length === 0

  // While loading with no rows yet, tuck a small spinner into the (otherwise
  // empty) last header cell — the one above the per-row edit/retry column — so
  // the table shows it's working without a separate full-width message block.
  const columns = showLoading
    ? INDEX_LIST_COLUMNS.map((col, idx) =>
        idx === INDEX_LIST_COLUMNS.length - 1
          ? { ...col, header: <ProgressSpinner size='lg' /> }
          : col
      )
    : INDEX_LIST_COLUMNS

  const requeue = async (toRetry: FlatIndex[]) => {
    const workspaceUuid = currentWorkspace?.uuid
    if (!workspaceUuid || toRetry.length === 0) return
    await Promise.all(
      toRetry.map(index =>
        updateIndexStatus(workspaceUuid, { id: index.id, status: IndexStatus.INITIALIZED })
      )
    )
    refetch()
  }

  return (
    <div className=''>
      {/* Toolbar: search on the left, bulk retry on the right (only when repos have failed) */}
      <div className='pl-[7px] pr-1 py-2 mb-4 flex items-center justify-between gap-2'>
        <SearchInput
          value={searchKeyword}
          placeholder='Search repository'
          onChange={setSearchKeyword}
          onClear={() => setSearchKeyword('')}
          className='w-[280px]'
        />
        {failedIndexes.length > 0 && (
          <Button
            variant='secondary'
            leftIcon={<RotateCw size={16} />}
            onClick={() => void requeue(failedIndexes)}
          >
            Retry all failed ({failedIndexes.length})
          </Button>
        )}
      </div>

      <div className='min-w-[480px] w-full'>
        <ColumnedList columns={columns} className='min-w-[480px] w-full'>
          {filteredIndexes.map(index => (
            <ColumnedListItem
              key={index.id}
              cells={[
                `${index.org_name}/${index.repo_name}/${index.branch_name}`,
                <StatusCell key='status' status={index.status} />,
                buildRuntimeText(index),
                index.status === IndexStatus.FAILED ? (
                  <button
                    key='retry'
                    type='button'
                    title='Retry indexing'
                    aria-label='Retry indexing'
                    className='w-5 h-5 flex justify-center items-center text-[rgba(0,0,0,0.45)] hover:text-[rgba(0,0,0,0.65)]'
                    onClick={e => {
                      e.stopPropagation()
                      void requeue([index])
                    }}
                  >
                    <RotateCw size={14} />
                  </button>
                ) : (
                  <div key='edit' className='w-5 h-5 flex justify-center items-center'>
                    <EditGrayIcon />
                  </div>
                )
              ]}
              onClick={() => onIndexSelect(index)}
            />
          ))}
        </ColumnedList>

        {showEmpty && (
          <div className='flex items-center justify-center py-10 text-sm text-[rgba(0,0,0,0.45)]'>
            No repositories indexed yet.
          </div>
        )}
        {showNoSearchMatches && (
          <div className='flex items-center justify-center py-10 text-sm text-[rgba(0,0,0,0.45)]'>
            No repositories match “{searchKeyword}”.
          </div>
        )}
      </div>
    </div>
  )
}
